// Variant sections refresh — update variant-dependent content in sibling sections
// on variant change (sections outside main-product, which the main-product
// re-render in theme.js never reaches).
//
// Two swap modes, per subtree:
//
// 1. IMAGE-ONLY (default): copy the new image src/srcset onto the existing <img>
//    nodes, matched by document order. For sections whose text/structure is
//    product-level and whose JS must survive (core-technologies measures tile
//    widths and translateX on scroll — re-initialising that mid-scroll
//    mis-measures and the tiles fly off-screen).
//
// 2. SUBTREE SWAP (opt-in, `data-variant-swap` on the subtree root): the fresh
//    subtree replaces the live one wholesale — for sections whose variant-
//    dependent content is more than imagery (the datasheet's spec VALUES
//    re-resolve per variant, AAS-5016). A custom element root re-runs
//    connectedCallback on insert, so its JS re-initialises cleanly. Only safe
//    for components that fully re-init from scratch; scroll-driven carousels
//    stay on mode 1.
//
// Shopify-standard fetch: Section Rendering API (?section_id=<id>&variant=<id>),
// which renders the section for the selected variant (it reads
// product.selected_or_first_available_variant, resolved by ?variant=).
//
// Opt in: add `data-variant-section` to any element inside the section
// (+ `data-variant-swap` on each subtree that should replace, not image-copy).
(() => {
  if (window.__variantSectionsRefresh) return;
  window.__variantSectionsRefresh = true;

  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  const IMG_SEL = 'img';
  // Copied alongside src/srcset/sizes when an image actually changes.
  const ATTRS = ['alt', 'width', 'height'];

  // AAS-4992: one refresh generation at a time. This fires one fetch per opted-in
  // sibling section, so a variant change that lands while an earlier one's
  // fetches are still open left both generations competing for connections with
  // the main-product render the shopper is actually waiting on — and both able
  // to write images once they landed. A newer change now aborts the older
  // generation outright; the token catches responses that were already in the
  // microtask queue.
  let generation = 0;
  let controller = null;

  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    const variant = data && data.variant;
    if (!variant) return;

    const token = ++generation;
    if (controller) controller.abort();
    controller = new AbortController();
    const signal = controller.signal;

    const wrappers = [];
    document.querySelectorAll('[data-variant-section]').forEach((el) => {
      const wrap = el.closest('.shopify-section');
      if (wrap && wrap.id && wrappers.indexOf(wrap) === -1) wrappers.push(wrap);
    });
    if (!wrappers.length) return;

    const selects = document.querySelector('variant-selects');
    const baseUrl = (selects && selects.dataset && selects.dataset.url) || window.location.pathname;

    wrappers.forEach((wrap) => {
      const sectionId = wrap.id.replace(/^shopify-section-/, '');
      const url = `${baseUrl}?section_id=${encodeURIComponent(sectionId)}&variant=${variant.id}`;

      fetch(url, { signal })
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((text) => {
          if (!text || !text.trim()) return;
          // Stale guard: variant changed again mid-flight. The generation token
          // also covers an A → B → A click run, where the ids match but this
          // response is still the abandoned one.
          if (token !== generation) return;
          if (selects && selects.currentVariant && selects.currentVariant.id !== variant.id) return;

          const doc = new DOMParser().parseFromString(text, 'text/html');
          const scope = doc.getElementById(wrap.id) || doc.body;

          /* Mode 2 — opt-in subtree swap (see file header). All-or-nothing on
           * count like the image path: a restructured section is skipped, not
           * mangled. importNode clones out of the parsed doc, so the fresh
           * copies survive for the image pairing below (their srcs then match
           * the just-swapped live nodes and are skipped as unchanged). */
          const freshSwaps = [...scope.querySelectorAll('[data-variant-swap]')];
          if (freshSwaps.length) {
            const liveSwaps = [...wrap.querySelectorAll('[data-variant-swap]')];
            if (freshSwaps.length === liveSwaps.length) {
              liveSwaps.forEach((live, i) => {
                live.replaceWith(document.importNode(freshSwaps[i], true));
              });
            }
          }

          /* Mode 1 — pair fresh <img> with live <img>.
           *
           * AAS-4992: this used to be one wrapper-wide index match guarded by
           * `freshImgs.length === liveImgs.length`, and on other-features that
           * guard could never pass. Its <modal-dialog id="OfModal-…"> is hoisted
           * to <body> at connect time (other-features.js — the fixed overlay has
           * to escape the section's stacking context), taking every
           * of-modal__detail-img with it. So the FRESH markup still held the
           * modal's images while the LIVE wrapper no longer did, the counts
           * never lined up, and the section silently never refreshed: the
           * of-media image loaded once on page load and then stayed on the
           * first variant's asset for good.
           *
           * Fix: match id'd subtrees wherever they now live — a hoisted node
           * keeps its id — then match whatever is left by document order inside
           * the wrapper. Each bucket is still all-or-nothing on count, so a
           * genuinely restructured section is skipped rather than mangled. */
          const pairs = [];
          const claimedFresh = new Set();
          const claimedLive = new Set();

          for (const freshHost of scope.querySelectorAll('[id]')) {
            const liveHost = document.getElementById(freshHost.id);
            if (!liveHost || liveHost === wrap) continue;
            // A hoisted node sits at body level (no .shopify-section ancestor).
            // Anything owned by a DIFFERENT section is an id collision, not our
            // node — never write images into someone else's section.
            const hostSection = liveHost.closest('.shopify-section');
            if (hostSection && hostSection !== wrap) continue;
            const hostFresh = [...freshHost.querySelectorAll(IMG_SEL)];
            if (!hostFresh.length) continue;
            // Nested id'd hosts: the outermost wins, so skip anything it took.
            if (hostFresh.some((img) => claimedFresh.has(img))) continue;
            const hostLive = [...liveHost.querySelectorAll(IMG_SEL)];
            if (hostFresh.length !== hostLive.length) continue; // moved on → leave it alone
            hostFresh.forEach((img, i) => {
              claimedFresh.add(img);
              claimedLive.add(hostLive[i]);
              pairs.push([img, hostLive[i]]);
            });
          }

          const restFresh = [...scope.querySelectorAll(IMG_SEL)].filter((img) => !claimedFresh.has(img));
          const restLive = [...wrap.querySelectorAll(IMG_SEL)].filter((img) => !claimedLive.has(img));
          if (restFresh.length === restLive.length) {
            restFresh.forEach((img, i) => pairs.push([img, restLive[i]]));
          }
          if (!pairs.length) return;

          pairs.forEach(([fresh, live]) => {
            const nextSrc = fresh.getAttribute('src');
            if (!nextSrc || live.getAttribute('src') === nextSrc) return; // unchanged → skip
            const nextSrcset = fresh.getAttribute('srcset');
            const nextSizes = fresh.getAttribute('sizes');
            live.setAttribute('src', nextSrc);
            if (nextSrcset) live.setAttribute('srcset', nextSrcset);
            else live.removeAttribute('srcset');
            if (nextSizes) live.setAttribute('sizes', nextSizes);
            // alt + intrinsic dims belong to the asset, not the slot: swapping
            // between two different assets (e.g. the datasheet's placeholder SVG
            // → a PIM sketch) changes both, and a stale width/height leaves the
            // wrong pre-load aspect-ratio box behind.
            ATTRS.forEach((name) => {
              const next = fresh.getAttribute(name);
              if (next === null) live.removeAttribute(name);
              else if (live.getAttribute(name) !== next) live.setAttribute(name, next);
            });
          });
        })
        .catch(() => {
          /* leave the existing section in place on failure */
        });
    });
  });
})();
